import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createGeschaeftspartner,
  type Geschaeftspartner,
  type GeschaeftspartnerIn,
  type GeschaeftspartnerTyp,
} from "@/lib/kontakte";
import { PartnerNameFields } from "@/pages/kontakte/PartnerNameFields";
import {
  emptyPartnerName,
  partnerNamePayload,
  validatePartnerName,
  type PartnerNameForm,
} from "@/pages/kontakte/partnerart";

interface GpCreateForm extends PartnerNameForm {
  email: string;
  telefon: string;
  adresszeile1: string;
  adresszeile2: string;
  strasse: string;
  plz: string;
  ort: string;
  kundennummer: string;
  kundencode: string;
  lieferantennummer: string;
  notizen: string;
}

const EMPTY_FORM: GpCreateForm = {
  ...emptyPartnerName(),
  email: "",
  telefon: "",
  adresszeile1: "",
  adresszeile2: "",
  strasse: "",
  plz: "",
  ort: "",
  kundennummer: "",
  kundencode: "",
  lieferantennummer: "",
  notizen: "",
};

export function GeschaeftspartnerCreateDialog({
  open,
  typ,
  onClose,
  onCreated,
}: {
  open: boolean;
  typ: GeschaeftspartnerTyp;
  onClose: () => void;
  onCreated: (gp: Geschaeftspartner) => void;
}) {
  const [form, setForm] = useState<GpCreateForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const typLabel = typ === "kunde" ? "Kunde" : "Lieferant";

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError(null);
    }
  }, [open]);

  function set<K extends keyof GpCreateForm>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    const nameError = validatePartnerName(form, typ);
    if (nameError) {
      setError(nameError);
      return;
    }
    let kundencode: number | null = null;
    if (typ === "kunde") {
      const rawCode = form.kundencode.trim();
      if (rawCode) {
        const parsed = Number.parseInt(rawCode, 10);
        if (!Number.isFinite(parsed) || parsed < 1) {
          setError("Bitte einen gültigen Kundencode eingeben (positive Zahl).");
          return;
        }
        kundencode = parsed;
      }
    }
    const payload: GeschaeftspartnerIn = {
      ...partnerNamePayload(form, typ),
      email: form.email.trim() || null,
      telefon: form.telefon.trim() || null,
      adresszeile1: form.adresszeile1.trim() || null,
      adresszeile2: form.adresszeile2.trim() || null,
      strasse: form.strasse.trim() || null,
      plz: form.plz.trim() || null,
      ort: form.ort.trim() || null,
      ...(typ === "kunde"
        ? {
            kundencode,
            kundennummer: form.kundennummer.trim() || null,
          }
        : {
            lieferantennummer: form.lieferantennummer.trim() || null,
          }),
      notizen: form.notizen.trim() || null,
    };
    setSaving(true);
    setError(null);
    try {
      const created = await createGeschaeftspartner(typ, payload);
      toast.success(`${typLabel} angelegt.`);
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${typLabel} anlegen`}
      dirty={JSON.stringify(form) !== JSON.stringify(EMPTY_FORM)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Anlegen…" : "Anlegen"}
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="Eingabe" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {typ === "kunde" ? (
          <>
            <TextInput
              label="Kundennummer"
              value={form.kundennummer}
              onChange={(e) => set("kundennummer", e.target.value)}
              hint="Muss eindeutig sein – doppelte Kundennummern werden abgelehnt."
            />
            <TextInput
              label="Kundencode"
              type="number"
              min={1}
              value={form.kundencode}
              onChange={(e) => set("kundencode", e.target.value)}
              hint="Interne Projektnummern-Gruppe – nicht die Kundennummer"
            />
          </>
        ) : (
          <TextInput
            label="Lieferantennummer"
            value={form.lieferantennummer}
            onChange={(e) => set("lieferantennummer", e.target.value)}
            hint="Muss eindeutig sein – doppelte Lieferantennummern werden abgelehnt."
            className="sm:col-span-2"
          />
        )}
        <PartnerNameFields
          typ={typ}
          form={form}
          onChange={(name) => setForm((current) => ({ ...current, ...name }))}
        />
        <TextInput
          label="1. Adresszeile"
          value={form.adresszeile1}
          onChange={(e) => set("adresszeile1", e.target.value)}
          hint="Namenszusatz oder zusätzliche Adresszeile wie in Hero"
        />
        <TextInput
          label="2. Adresszeile"
          value={form.adresszeile2}
          onChange={(e) => set("adresszeile2", e.target.value)}
        />
        <TextInput
          label="E-Mail"
          type="email"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
        <TextInput
          label="Telefon"
          value={form.telefon}
          onChange={(e) => set("telefon", e.target.value)}
        />
        <TextInput
          label="Straße"
          value={form.strasse}
          onChange={(e) => set("strasse", e.target.value)}
          className="sm:col-span-2"
        />
        <TextInput label="PLZ" value={form.plz} onChange={(e) => set("plz", e.target.value)} />
        <TextInput label="Ort" value={form.ort} onChange={(e) => set("ort", e.target.value)} />
        <TextareaInput
          label="Notizen"
          value={form.notizen}
          onChange={(e) => set("notizen", e.target.value)}
          rows={4}
          className="sm:col-span-2"
          hint="Interne Hinweise zu diesem Kontakt."
        />
      </div>
    </Dialog>
  );
}
